// Monitoreo de errores con Sentry, DESACTIVADO mientras no exista VITE_SENTRY_DSN.
// - Sin DSN: no se importa el SDK, no se crea ninguna conexión, `captureError` no hace nada.
// - Con DSN: el SDK se carga con import() dinámico después de que la página terminó de cargar
//   (queda en un chunk aparte; no pesa en el bundle inicial ni en las páginas públicas).
// Privacidad: sin PII, sin Session Replay, sin tracing, sin breadcrumbs de consola/red; todo
//   pasa por sentry-scrub.ts. Antes de activarlo: ver sección M de docs/legal/checklist-legal-chile-2026.md.
import { IGNORAR_ERRORES, IGNORAR_URLS, scrubBreadcrumb, scrubEvent } from "./sentry-scrub";

type Rol = "admin" | "profesor" | "alumno" | "visitante";
type Sdk = typeof import("@sentry/react");
type Cargador = () => Promise<Sdk>;

const MAX_COLA = 20;

export function crearMonitor(opts: { dsn?: string; entorno: string; release?: string; cargar: Cargador }) {
  const activo = !!opts.dsn;
  let sdk: Sdk | null = null;
  let iniciado = false;
  let rol: Rol | undefined;
  const cola: Array<[unknown, Record<string, unknown> | undefined]> = [];

  function enviar(s: Sdk, error: unknown, ctx?: Record<string, unknown>) {
    s.withScope((scope) => {
      if (ctx?.source) scope.setTag("source", String(ctx.source));
      s.captureException(error);
    });
  }

  return {
    activo,
    /** Carga el SDK (una sola vez). Sin DSN no hace nada. */
    async iniciar() {
      if (!activo || iniciado) return;
      iniciado = true;
      try {
        const s = await opts.cargar();
        s.init({
          dsn: opts.dsn,
          environment: opts.entorno,
          release: opts.release,
          // En v11 sendDefaultPii se reemplazó por dataCollection: todo apagado.
          dataCollection: { userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false },
          tracesSampleRate: 0,
          defaultIntegrations: false,
          integrations: [
            s.dedupeIntegration(),
            s.eventFiltersIntegration(),
            s.functionToStringIntegration(),
            s.linkedErrorsIntegration(),
            // Sin consola, fetch, xhr ni clics. Tampoco globalHandlers: los listeners ya los pone main.tsx
            // y llaman a reportLovableError (así no se envía dos veces). Sin Replay ni tracing.
            s.breadcrumbsIntegration({ fetch: false, xhr: false, dom: false, history: true, sentry: false }),
          ],
          ignoreErrors: IGNORAR_ERRORES,
          denyUrls: IGNORAR_URLS,
          beforeSend: (ev) => scrubEvent(ev as unknown as Parameters<typeof scrubEvent>[0], rol) as unknown as typeof ev,
          beforeBreadcrumb: (b) => scrubBreadcrumb(b),
        });
        sdk = s;
        for (const [e, c] of cola.splice(0)) enviar(s, e, c);
      } catch {
        iniciado = false; // fallo de carga (sin red, bloqueador): se reintentará en el próximo error
        cola.length = 0;
      }
    },
    capturar(error: unknown, ctx?: Record<string, unknown>) {
      if (!activo) return;
      if (sdk) return enviar(sdk, error, ctx);
      if (cola.length < MAX_COLA) cola.push([error, ctx]);
      void this.iniciar();
    },
    /** Solo el rol, nunca datos personales. */
    fijarRol(r: Rol) { rol = r; },
  };
}

const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim() || undefined;

export const monitor = crearMonitor({
  dsn,
  entorno: typeof location !== "undefined" && (location.hostname.endsWith("cprogresiva.cl") || location.hostname.endsWith(".web.app")) ? "production" : "local",
  release: (import.meta.env.VITE_APP_RELEASE as string | undefined) || undefined,
  cargar: () => import("@sentry/react"),
});

/** Llamar una vez desde main.tsx: carga el SDK cuando la página ya terminó de cargar. */
export function iniciarSentry() {
  if (!monitor.activo) return;
  const lanzar = () => (window.requestIdleCallback ?? ((f: () => void) => setTimeout(f, 2000)))(() => void monitor.iniciar());
  if (document.readyState === "complete") lanzar();
  else window.addEventListener("load", lanzar, { once: true });
}
